import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Phone, Mail, ExternalLink, Clock, CheckCircle, AlertCircle, User, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import StatusBadge from '../../components/common/StatusBadge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function UserDashboard() {
  const { user, accessToken } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, ordersRes] = await Promise.all([
        axios.get(`${API}/user/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        axios.get(`${API}/payment/history`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        })
      ]);
      
      setDashboard(dashboardRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getEditionName = (edition) => {
    return edition === 'client' ? 'Client Edition' : 'Community Edition';
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-[#00B4B4]">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FBFD]">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-start">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A365D]">
                Welcome back, {dashboard?.firstName || user?.firstName}!
              </h1>
              <p className="text-slate-600 mt-1">
                Your IRS Peace-of-Mind Review Dashboard
              </p>
            </motion.div>
            <Link to="/profile">
              <Button variant="outline" className="flex items-center gap-2" data-testid="edit-profile-btn">
                <Settings className="h-4 w-4" />
                Edit Profile
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Review Status Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-1"
          >
            <Card data-testid="user-status-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Review Status</span>
                  <StatusBadge status={dashboard?.paymentStatus || 'paid'} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-[#F0FEFE] to-white rounded-lg border border-[#00B4B4]/20">
                    <p className="text-sm font-medium text-[#1A365D]">
                      {getEditionName(dashboard?.productEdition)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      IRS Peace-of-Mind Review
                    </p>
                  </div>
                  
                  {/* POA Status - Only shown for paid users */}
                  {dashboard?.paymentStatus === 'paid' && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-[#1A365D]">Power of Attorney</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {dashboard?.poaSigned ? 'Document signed and on file' : 'Required for IRS representation'}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${dashboard?.poaSigned ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="poa-status-badge">
                          {dashboard?.poaSigned ? 'Signed' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <h4 className="font-medium text-[#1A365D] text-sm">Next Steps</h4>
                    {dashboard?.nextSteps?.map((step, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-[#00B4B4] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Orders/Invoices Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <Card data-testid="user-invoices-table">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#00B4B4]" />
                  Your Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No orders found</p>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div 
                        key={order.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-lg gap-4"
                      >
                        <div>
                          <p className="font-medium text-[#1A365D] font-mono text-sm">
                            {order.orderNumber}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {getEditionName(order.productEdition)} • ${order.total.toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={order.paymentStatus} />
                          {order.invoiceUrl && (
                            <a 
                              href={order.invoiceUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                              data-testid="invoice-download-link"
                            >
                              <Download className="h-4 w-4" />
                              Invoice
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Support Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-3"
          >
            <Card>
              <CardHeader>
                <CardTitle>Need Help?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a 
                    href="tel:1-844-499-3355"
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#00B4B4]/10 rounded-full flex items-center justify-center">
                      <Phone className="h-5 w-5 text-[#00B4B4]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A365D]">Call Us</p>
                      <p className="text-sm text-slate-600">1-844-499-3355</p>
                    </div>
                  </a>
                  
                  <a 
                    href="mailto:relief@kgob.com"
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#00B4B4]/10 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-[#00B4B4]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1A365D]">Email Us</p>
                      <p className="text-sm text-slate-600">relief@kgob.com</p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
